import { useEffect, useRef, useState } from "react"
import { useRange } from "react-instantsearch"

import { Box } from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import dayjs, { nzLocaleText } from "@tc/ui-dfe/utils/libs/dayjs"
import { getStartAndEndTimestamp, isSameDay } from "./helper"

interface TypeSenseRefineDateProps {
  attribute: string
  clearFiltersToggle?: boolean
}

export function TypesenseRefineDate(props: Readonly<TypeSenseRefineDateProps>) {
  const { range, canRefine, refine, start: init } = useRange(props)
  const { min, max } = range
  const maxDate = Number(max)
  const [initStart, initEnd] = init
  const auto = useRef(true)
  const [dateFilter, setDateFilter] = useState({
    start: initStart !== -Infinity ? Number(initStart) : null,
    end: initEnd !== Infinity ? Number(initEnd) : null,
  })

  useEffect(() => {
    setDateFilter({
      start: initStart !== -Infinity ? Number(initStart) : null,
      end: initEnd !== Infinity ? Number(initEnd) : null,
    })
  }, [])

  // Reset local date state when the user clicks "Clear all".
  // clearFiltersToggle flips on an explicit clear (same pattern used by the
  // other refinement widgets), so this only fires on clear and never during
  // normal date selection. Skip the first run so an initial state loaded from
  // the URL isn't wiped on mount.
  const didMountClearToggle = useRef(false)
  useEffect(() => {
    if (!didMountClearToggle.current) {
      didMountClearToggle.current = true
      return
    }
    setDateFilter({ start: null, end: null })
  }, [props.clearFiltersToggle])

  useEffect(() => {
    //reset refinement if user clears both start and end date
    if (dateFilter.start === null && dateFilter.end === null) {
      refine([Number(min), Number(max)])
      return
    }

    // Only refine if user has actually selected dates
    if (dateFilter.start === null || dateFilter.end === null) {
      return
    }

    let start = Math.max(Number(min), Number(dateFilter.start))
    let end = Math.min(Number(max), Number(dateFilter.end))

    const { startTimestamp, endTimestamp } = getStartAndEndTimestamp(start)

    if (isSameDay(start, end)) {
      start = Math.max(startTimestamp, Number(min))
      end = Math.min(endTimestamp, Number(max))
    }

    // useRange ignores refine([min,max]) - adjust end by -1ms to force filter
    // Safe since all timestamps end with 000,
    // and frontend filter's end date will always end in 999
    if (start === Number(min) && end === Number(max)) {
      end = Number(max) - 1
    }

    if (start && end) {
      refine([start, end])
    }
  }, [dateFilter, range])

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flexFlow: "nowrap",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "0.8rem",
        marginTop: "1rem",
      }}
    >
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale="en-nz"
        localeText={nzLocaleText}
      >
        <DatePicker
          disabled={!canRefine}
          format="DD/MMM/YYYY"
          value={
            dateFilter.start
              ? dayjs(dateFilter.start).tz("Pacific/Auckland")
              : null
          }
          minDate={dayjs(min).tz("Pacific/Auckland")}
          maxDate={dayjs(maxDate).tz("Pacific/Auckland")}
          onChange={(newValue) => {
            auto.current = false
            const start = newValue
              ?.tz("Pacific/Auckland")
              .startOf("day")
              .valueOf()
            setDateFilter({
              ...dateFilter,
              start: start ?? null,
            })
          }}
          label="From"
          sx={{ width: "16vw", marginBottom: "1rem" }}
          slotProps={{
            textField: {
              error: false,
              helperText: null,
              InputLabelProps: { shrink: true },
            },
          }}
        ></DatePicker>
        <DatePicker
          disabled={!canRefine}
          format="DD/MMM/YYYY"
          value={
            dateFilter.end ? dayjs(dateFilter.end).tz("Pacific/Auckland") : null
          }
          minDate={dayjs(min).tz("Pacific/Auckland")}
          maxDate={dayjs(maxDate).tz("Pacific/Auckland")}
          sx={{ width: "16vw" }}
          onChange={(newValue) => {
            auto.current = false

            const end = newValue?.tz("Pacific/Auckland").endOf("day").valueOf()

            setDateFilter({
              ...dateFilter,
              end: end ?? null,
            })
          }}
          label="To"
          slotProps={{
            textField: {
              error: false,
              helperText: null,
              InputLabelProps: { shrink: true },
            },
          }}
        ></DatePicker>
      </LocalizationProvider>
    </Box>
  )
}
